// ============================================================
// GAUGE - an arc on an ellipse, and ticks inside one.
//
// Geometry only. It knows about ellipses and fractions and nothing at
// all about wounds, ammo or health: `markerStatus.ts` decides what a
// fraction MEANS, this decides where it goes.
//
// TURN IS MEASURED CLOCKWISE FROM THE TOP, IN SCREEN SPACE. Not world
// space - a reader will assume the top of the ring is north on the map,
// and it is not. It is screen-up, which on the ground plane is "away
// from the camera" and swings around as the map is orbited. That is the
// right choice: a gauge that rotated with the yaw would be unreadable
// from half the angles, and every other readout on this map is in
// screen space too.
//
// Pure. Zero React dependency.
// ============================================================

export interface Ring {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

/** How far outside the footprint the gauge ring sits, in viewBox units. */
export const GAUGE_GAP = 3;

const TAU = Math.PI * 2;

/**
 * Below this, the two ends of a "full" arc coincide.
 *
 * An SVG arc whose end equals its start draws NOTHING, so a full ring
 * built from one `A` command is invisible - which reads as a broken
 * feature rather than an undamaged combatant. Anything this close to a
 * whole turn is snapped to the two-arc form.
 */
const FULL_EPSILON = 1e-6;

const ok = (n: number) => Number.isFinite(n) && n > 0;
const usable = (ring: Ring) => ok(ring.rx) && ok(ring.ry) && Number.isFinite(ring.cx) && Number.isFinite(ring.cy);

/** Trimmed to 2dp: a path is a string, and full float precision bloats every marker. */
const fx = (n: number) => Number(n.toFixed(2));

/**
 * A point on the ring at `turn` (0..1) clockwise from the top, scaled
 * inward by `s` (1 = on the ring, 0 = at the centre).
 *
 * SIN on x and COS on y, not the other way about. The footprint ellipse
 * above this one was once foreshortened by cos instead of sin and
 * collapsed to a 1.5-pixel line that nobody spotted until markers could
 * not be grabbed; the same mistake here draws a gauge that drains
 * anticlockwise from 3 o'clock and looks perfectly plausible.
 */
export function ringPoint(ring: Ring, turn: number, s = 1): { x: number; y: number } {
  const a = turn * TAU;
  return {
    x: ring.cx + s * ring.rx * Math.sin(a),
    // Minus, because screen y grows DOWNWARD: this is what puts turn 0
    // at the top and makes the sweep clockwise.
    y: ring.cy - s * ring.ry * Math.cos(a),
  };
}

/**
 * The SVG `d` for an arc from `fromTurn`, clockwise, covering `span`
 * turns.
 *
 * Returns '' when there is nothing to draw, so a caller can skip the
 * element entirely rather than render `d=""` - a node that paints
 * nothing but still costs one per marker.
 *
 * `fromTurn` is only an angle, so a span may run past a whole turn
 * (0.9 → 1.1 crosses twelve o'clock) with no wrapping case: `ringPoint`
 * is periodic and does the right thing.
 */
export function arcSegment(ring: Ring, fromTurn: number, span: number): string {
  if (!usable(ring) || !Number.isFinite(fromTurn) || !Number.isFinite(span)) return '';
  const s = Math.min(1, Math.max(0, span));
  if (s <= FULL_EPSILON) return '';

  const from = ringPoint(ring, fromTurn);
  const r = `${fx(ring.rx)} ${fx(ring.ry)}`;
  const move = `M ${fx(from.x)} ${fx(from.y)}`;

  if (s >= 1 - FULL_EPSILON) {
    const half = ringPoint(ring, fromTurn + 0.5);
    return `${move} A ${r} 0 0 1 ${fx(half.x)} ${fx(half.y)} A ${r} 0 0 1 ${fx(from.x)} ${fx(from.y)}`;
  }

  const end = ringPoint(ring, fromTurn + s);
  // Rotation 0 always: the projected footprint never tilts, because
  // `project()` is linear and separable in screen x and y - which is
  // why the footprint can be an <ellipse> at all. Sweep 1 always: the
  // positive-angle direction, clockwise under a downward y.
  return `${move} A ${r} 0 ${s > 0.5 ? 1 : 0} 1 ${fx(end.x)} ${fx(end.y)}`;
}

/** The special case that starts at the top: a gauge filling from twelve o'clock. */
export function arcPath(ring: Ring, fraction: number): string {
  return arcSegment(ring, 0, fraction);
}
